export type VersionStatus = {
  serverVersion: string | null;
  clientVersion: string;
  /** Git commit SHA of the running server build; null until the WS handshake arrives or when the host bakes no commit. */
  serverCommit: string | null;
  /** Git commit SHA baked into this client bundle; empty in local dev. */
  clientCommit: string;
  updateAvailable: boolean;
  pendingRefresh: boolean;
  blockerCount: number;
};

export type VersionStatusListener = (status: VersionStatus) => void;

const RETRY_INTERVAL_MS = 10_000;

export class VersionRefreshManager {
  private serverVersion: string | null = null;
  private serverCommit: string | null = null;
  private readonly clientVersion: string = __APP_VERSION__;
  private readonly clientCommit: string = __COMMIT_HASH__;
  private pendingRefresh = false;
  private blockers = new Set<string>();
  private listeners = new Set<VersionStatusListener>();
  private cachedStatus: VersionStatus;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private checkInFlight = false;

  /** Skip version-mismatch detection. Defaults to true in dev mode. */
  skipVersionCheck: boolean = import.meta.env.DEV;

  /** Stable reference for useSyncExternalStore's subscribe arg. */
  readonly subscribe: (cb: VersionStatusListener) => () => void;
  /** Stable reference for useSyncExternalStore's getSnapshot arg. */
  readonly getSnapshot: () => VersionStatus;

  constructor() {
    this.cachedStatus = this.buildStatus();
    this.subscribe = (cb) => this.onStatusChange(cb);
    this.getSnapshot = () => this.cachedStatus;
  }

  getStatus(): VersionStatus {
    return this.cachedStatus;
  }

  onStatusChange(listener: VersionStatusListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onVersionMessage(serverVersion: string, serverCommit?: string): void {
    this.serverVersion = serverVersion;
    this.serverCommit = serverCommit ?? null;
    if (serverVersion === this.clientVersion) {
      this.pendingRefresh = false;
      this.clearRetry();
      this.notify();
      return;
    }

    if (this.skipVersionCheck) {
      this.notify();
      return;
    }

    this.pendingRefresh = true;
    this.notify();
    void this.tryRefresh();
  }

  addBlocker(id: string): void {
    this.blockers.add(id);
    this.notify();
  }

  removeBlocker(id: string): void {
    this.blockers.delete(id);
    this.notify();
    void this.tryRefresh();
  }

  private buildStatus(): VersionStatus {
    return {
      serverVersion: this.serverVersion,
      clientVersion: this.clientVersion,
      serverCommit: this.serverCommit,
      clientCommit: this.clientCommit,
      updateAvailable:
        !this.skipVersionCheck &&
        this.serverVersion !== null &&
        this.serverVersion !== this.clientVersion,
      pendingRefresh: this.pendingRefresh,
      blockerCount: this.blockers.size,
    };
  }

  /**
   * Fetches /version.json (cache-busted) to verify that the static assets
   * have actually been deployed at the version the server expects. Only
   * reloads if the hosted bundle version matches the server version;
   * otherwise schedules a retry so we don't loop on stale CDN caches.
   */
  private async tryRefresh(): Promise<void> {
    if (!this.pendingRefresh || this.blockers.size > 0 || this.checkInFlight) return;

    this.checkInFlight = true;
    try {
      const resp = await fetch(`/version.json?_t=${Date.now()}`, { cache: 'no-store' });
      if (!resp.ok) {
        this.scheduleRetry();
        return;
      }
      const data: unknown = await resp.json();
      const hostedVersion =
        typeof data === 'object' && data !== null && 'version' in data
          ? (data as { version: unknown }).version
          : null;

      if (hostedVersion === this.serverVersion) {
        window.location.reload();
      } else {
        this.scheduleRetry();
      }
    } catch {
      this.scheduleRetry();
    } finally {
      this.checkInFlight = false;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.tryRefresh();
    }, RETRY_INTERVAL_MS);
  }

  private clearRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private notify(): void {
    this.cachedStatus = this.buildStatus();
    for (const listener of this.listeners) {
      try {
        listener(this.cachedStatus);
      } catch {
        // listener errors should not propagate
      }
    }
  }
}

let instance: VersionRefreshManager | null = null;

export function getVersionRefreshManager(): VersionRefreshManager {
  if (!instance) {
    instance = new VersionRefreshManager();
  }
  return instance;
}
